import React, { useState, useEffect } from 'react';
import { 
  CreditCard,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Calendar,
  AlertCircle,
  Shield,
  Clock,
  MapPin,
  Sparkles
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotification } from '@/contexts/NotificationContext';
import { redirectToPortal, updateSubscriptionQuantity } from '@/services/stripeService';
import { createCheckoutSession, redirectToCheckout } from '@/services/stripeService';
import { getLoginUrl } from '@/utils/domainUtils';
import { useNavigate } from 'react-router-dom';
import { getUserRole } from '@/services/taskService';
import { getUserSystemRoleForOrganization, isAdminInOrganization } from '@/utils/roleHelpers';
import { locationService } from '@/firebase/locationService';
import { normalizeSubscriptionPlan } from '@/firebase/subscriptionService';

// Import ChatGPT logo
const chatGPTLogo = '/chatgpt-logo.png';

export default function SubscriptionManagement() {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const { subscription, isLoading, hasActiveSubscription, refreshSubscription } = useSubscription();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [locationCount, setLocationCount] = useState<number>(0);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false); // Modal to enter quantity
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null); // Plan selected for checkout
  const [quantityInput, setQuantityInput] = useState<number>(1); // Quantity input value
  const [showUpdateQuantityModal, setShowUpdateQuantityModal] = useState(false); // Modal to update existing subscription quantity
  const [updateQuantityInput, setUpdateQuantityInput] = useState<number>(1); // Quantity input for update
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false); // Loading state for updating quantity
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // Modal to show upgrade warning
  const [upgradeModalMessage, setUpgradeModalMessage] = useState<string>(''); // Message for upgrade modal
  const [showOrgModal, setShowOrgModal] = useState(false); // Modal to show organization required
  const [chatGPTLogoError, setChatGPTLogoError] = useState(false);

  // Debug: Log subscription and organization info
  useEffect(() => {
    console.log('🔍 Subscription Debug Info:', {
      userEmail: user?.email,
      userId: user?.id,
      currentOrganizationId: currentOrganization?.id,
      currentOrganizationName: currentOrganization?.name,
      subscription: subscription,
      hasActiveSubscription: hasActiveSubscription,
      isLoading: isLoading,
    });
  }, [user, currentOrganization, subscription, hasActiveSubscription, isLoading]);

  // Check if user is admin for current organization
  useEffect(() => {
    const checkRole = async () => {
      if (!user?.id) {
        setIsCheckingRole(false);
        return;
      }
      
      if (!currentOrganization?.id) {
        setIsCheckingRole(false);
        return;
      }
      
      try {
        // Get organization-specific system role
        const { db } = await import('@/firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          // Get organization-specific system role
          const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
          setUserRole(systemRole || null);
        } else {
          // Fallback to legacy getUserRole
          const role = await getUserRole(user.id);
          setUserRole(role);
        }
      } catch (error) {
        console.error('Error checking user role:', error);
        setUserRole(null);
      } finally {
        setIsCheckingRole(false);
      }
    };
    checkRole();
  }, [user?.id, currentOrganization?.id, currentOrganization?.name]);

  // Redirect non-admin users
  useEffect(() => {
    if (!isCheckingRole && userRole !== 'admin' && isAuthenticated) {
      console.log(`[SubscriptionManagement] Redirecting non-admin user (role: ${userRole}) to home`);
      navigate('/home');
    }
  }, [userRole, isCheckingRole, isAuthenticated, navigate]);

  // Load location count
  useEffect(() => {
    const loadLocationCount = async () => {
      if (!currentOrganization?.id) {
        setLocationCount(0);
        return;
      }

      setIsLoadingLocations(true);
      try {
        const result = await locationService.getAllLocations(currentOrganization.id);
        if (result.success && result.data) {
          setLocationCount(result.data.length);
        } else {
          setLocationCount(0);
        }
      } catch (error) {
        console.error('Error loading locations:', error);
        setLocationCount(0);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    loadLocationCount();
  }, [currentOrganization?.id]);

  const handleManageSubscription = async () => {
    if (!user?.id || !currentOrganization?.id) return;

    setIsLoadingPortal(true);
    try {
      await redirectToPortal(user.id, currentOrganization.id);
      // Refresh subscription after returning from portal
      setTimeout(() => {
        refreshSubscription();
      }, 2000);
    } catch (error: any) {
      console.error('Error opening portal:', error);
      
      // Provide helpful error message and guidance
      let errorMessage = error.message || 'Unknown error';
      if (errorMessage.includes('No Stripe customer') || errorMessage.includes('No valid Stripe customer')) {
        errorMessage += '\n\nSince you just switched to production mode, you need to create a new subscription. Test mode customers do not transfer to production.';
        
        // Note: Plans are always visible for free tier users, no need to toggle
      } else {
        alert(`Failed to open subscription portal: ${errorMessage}`);
      }
    } finally {
      setIsLoadingPortal(false);
    }
  };


  // Show loading while checking role
  if (isCheckingRole || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show access denied for non-admin users
  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            Subscription management is only available to administrators.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };


  const handleChoosePlan = async (planName: string, quantity?: number) => {
    // Handle free Communicate tier
    if (planName.toLowerCase() === 'communicate') {
      if (!isAuthenticated || !user) {
        window.location.href = getLoginUrl(`signup=true&plan=communicate`);
        return;
      }
      // For free tier, just show message - no checkout needed
      alert('Communicate tier is free! You can start using team communication features right away.');
      return;
    }

    // If not authenticated, redirect to login with plan parameter
    if (!isAuthenticated || !user) {
      window.location.href = getLoginUrl(`signup=true&plan=${planName.toLowerCase()}`);
      return;
    }

    // Validate user has an ID
    if (!user.id) {
      alert('User ID is missing. Please log out and log back in.');
      return;
    }

    // If quantity not provided, show modal to get quantity
    if (!quantity) {
      setSelectedPlan(planName);
      setQuantityInput(locationCount || 1); // Pre-fill with current location count or 1
      setShowQuantityModal(true);
      return;
    }

    // If authenticated, create Stripe checkout session
    setIsLoadingCheckout(planName.toLowerCase());
    try {
      console.log('Starting checkout for plan:', planName, 'Quantity:', quantity, 'User ID:', user.id);
      
      if (!currentOrganization?.id) {
        setIsLoadingCheckout(null);
        setShowOrgModal(true);
        return;
      }

      const result = await createCheckoutSession({
        plan: planName.toLowerCase(),
        userId: user.id,
        organizationId: currentOrganization.id,
        quantity: quantity,
      });

      console.log('Checkout session result:', result);

      // Use session.url for manual redirection (new Stripe.js approach)
      if (result.url) {
        await redirectToCheckout(result.url);
      } else if (result.sessionId) {
        // Fallback: if only sessionId is provided, try to construct URL (shouldn't happen with current server)
        throw new Error('Session URL not available. Please ensure the server returns session.url');
      } else {
        throw new Error('No checkout URL or session ID returned from checkout session creation');
      }
    } catch (error: any) {
      console.error('Error starting checkout:', error);
      
      // Provide user-friendly error messages
      let errorMessage = error.message || 'Unknown error';
      
      if (errorMessage.includes('User not found')) {
        errorMessage = 'Your user account could not be found. Please contact support or try logging out and back in.';
      } else if (errorMessage.includes('Cannot connect')) {
        errorMessage = 'Unable to connect to payment server. Please check your internet connection and try again.';
      } else if (errorMessage.includes('Invalid plan')) {
        errorMessage = 'The selected plan is not available. Please try selecting a different plan.';
      }
      
      alert(`Failed to start checkout: ${errorMessage}`);
    } finally {
      setIsLoadingCheckout(null);
    }
  };

  const handleConfirmQuantity = () => {
    if (quantityInput < 1) {
      alert('Please enter at least 1 location.');
      return;
    }
    if (selectedPlan) {
      setShowQuantityModal(false);
      handleChoosePlan(selectedPlan, quantityInput);
    }
  };

  const handleUpdateQuantity = async () => {
    if (!currentOrganization?.id || !subscription) {
      alert('Organization or subscription not found.');
      return;
    }

    if (updateQuantityInput < 1) {
      alert('Please enter at least 1 location.');
      return;
    }

    if (updateQuantityInput === subscription.quantity) {
      alert('The quantity is already set to this value.');
      setShowUpdateQuantityModal(false);
      return;
    }

    // Check if trying to downsize below current location count
    if (updateQuantityInput < locationCount) {
      setUpgradeModalMessage(
        `Cannot reduce location licenses to ${updateQuantityInput}. You currently have ${locationCount} location${locationCount !== 1 ? 's' : ''} in your system. Please remove ${locationCount - updateQuantityInput} location${locationCount - updateQuantityInput !== 1 ? 's' : ''} from your organization before reducing license count.`
      );
      setShowUpgradeModal(true);
      setShowUpdateQuantityModal(false);
      return;
    }

    setIsUpdatingQuantity(true);
    try {
      await updateSubscriptionQuantity(currentOrganization.id, updateQuantityInput);
      showNotification(`Subscription updated to ${updateQuantityInput} location${updateQuantityInput !== 1 ? 's' : ''}`, 'success');
      await refreshSubscription();
      setShowUpdateQuantityModal(false);
    } catch (error: any) {
      console.error('Error updating subscription quantity:', error);
      alert(`Failed to update subscription: ${error.message || 'Unknown error'}`);
    } finally {
      setIsUpdatingQuantity(false);
    }
  };

  // Calculate grace period remaining for past_due subscriptions
  const getGracePeriodRemaining = (subscription: any): number | null => {
    if (subscription?.status !== 'past_due') {
      return null;
    }

    const GRACE_PERIOD_DAYS = 7;
    const paymentFailedAt = subscription.paymentFailedAt || subscription.updatedAt;
    
    if (!paymentFailedAt) {
      return null;
    }

    try {
      const failedDate = paymentFailedAt.toDate ? paymentFailedAt.toDate() : new Date(paymentFailedAt);
      const daysSinceFailure = (Date.now() - failedDate.getTime()) / (1000 * 60 * 60 * 24);
      const daysRemaining = GRACE_PERIOD_DAYS - daysSinceFailure;
      return Math.max(0, Math.ceil(daysRemaining));
    } catch (error) {
      return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      active: { 
        color: 'bg-green-100 text-green-800 border-green-200', 
        icon: <CheckCircle2 className="w-4 h-4" />, 
        text: 'Active' 
      },
      trialing: { 
        color: 'bg-blue-100 text-blue-800 border-blue-200', 
        icon: <CheckCircle2 className="w-4 h-4" />, 
        text: 'Trialing' 
      },
      past_due: { 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
        icon: <AlertCircle className="w-4 h-4" />, 
        text: 'Past Due' 
      },
      canceled: { 
        color: 'bg-gray-100 text-gray-800 border-gray-200', 
        icon: <XCircle className="w-4 h-4" />, 
        text: 'Canceled' 
      },
      incomplete: { 
        color: 'bg-orange-100 text-orange-800 border-orange-200', 
        icon: <XCircle className="w-4 h-4" />, 
        text: 'Incomplete' 
      },
      incomplete_expired: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: <XCircle className="w-4 h-4" />,
        text: 'Expired'
      },
    };

    const config = statusConfig[status] || statusConfig.incomplete;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium border ${config.color}`}>
        {config.icon}
        {config.text}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription</h1>
          <p className="text-gray-600">Manage your subscription and billing</p>
        </div>
      </div>

      {/* Payment Warning Banner */}
      {subscription && subscription.status === 'past_due' && (() => {
        const gracePeriodRemaining = getGracePeriodRemaining(subscription);
        return (
          <div className="card bg-yellow-50 border-yellow-200 border-2">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">Payment Failed - Action Required</h3>
                <p className="text-sm text-yellow-800 mb-3">
                  We were unable to process your payment. Your subscription is in a <strong>past due</strong> status.
                </p>
                {gracePeriodRemaining !== null && gracePeriodRemaining > 0 ? (
                  <p className="text-sm text-yellow-800 mb-4">
                    <strong>Grace Period:</strong> You have <strong>{gracePeriodRemaining} day{gracePeriodRemaining !== 1 ? 's' : ''}</strong> remaining to update your payment method before access is restricted.
                  </p>
                ) : (
                  <p className="text-sm text-yellow-800 mb-4">
                    <strong>Warning:</strong> Your grace period has ended. Please update your payment method immediately to restore access.
                  </p>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={handleManageSubscription}
                    disabled={isLoadingPortal}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {isLoadingPortal ? 'Loading...' : 'Update Payment Method'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Incomplete Expired Warning */}
      {subscription && subscription.status === 'incomplete_expired' && (
        <div className="card bg-red-50 border-red-200 border-2">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Subscription Setup Expired</h3>
              <p className="text-sm text-red-800 mb-4">
                Your subscription setup has expired. Please create a new subscription to continue using the service.
              </p>
              <button
                onClick={() => {
                  // Plans are now always visible for free tier, scroll to them
                  const plansSection = document.querySelector('[data-plans-section]');
                  if (plansSection) {
                    plansSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                View Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Card */}
      <div className="card">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading subscription...</p>
          </div>
                ) : subscription && subscription.plan ? (
          <div className="space-y-6">
            {/* Plan Info */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-semibold text-gray-900 capitalize">{subscription?.plan || 'Unknown'} Plan</h2>
                  {/* Location Usage - Compact */}
                  {(hasActiveSubscription || subscription?.status === 'trialing') && subscription?.plan && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      {isLoadingLocations ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                      ) : (
                        <span className={`text-xs font-medium ${
                          locationCount >= (subscription?.quantity || 1)
                            ? 'text-red-600'
                            : 'text-gray-600'
                        }`}>
                          {locationCount} / {subscription?.quantity || 1} locations
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">Current subscription</p>
                {subscription?.status === 'trialing' && subscription?.currentPeriodEnd && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Trial ends {formatDate(subscription?.currentPeriodEnd)}
                  </p>
                )}
                {/* Subscription Details */}
                <div className="mt-3 space-y-1">
                  {subscription.currentPeriodStart && (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Period:</span> {formatDate(subscription.currentPeriodStart)} - {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'N/A'}
                    </p>
                  )}
                  {subscription.stripeSubscriptionId && (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">ID:</span> <span className="font-mono">{subscription.stripeSubscriptionId.substring(0, 24)}...</span>
                    </p>
                  )}
                </div>
              </div>
              {getStatusBadge(subscription?.status || 'incomplete')}
            </div>

            {/* Actions */}
            <div className="pt-4">
              <div className="flex gap-3">
                {subscription?.status === 'canceled' ? (
                  <>
                    <button
                      onClick={() => {
                        if (subscription?.plan) {
                          handleChoosePlan(subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1), subscription.quantity || locationCount || 1);
                        }
                      }}
                      disabled={isLoadingCheckout !== null || !subscription?.plan}
                      className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-xs font-medium"
                    >
                      {isLoadingCheckout ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-3 h-3" />
                          Renew Subscription
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleManageSubscription}
                      disabled={isLoadingPortal}
                      className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-xs font-medium"
                    >
                      {isLoadingPortal ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-3 h-3" />
                          Manage
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleManageSubscription}
                      disabled={isLoadingPortal}
                      className="flex-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-xs font-medium"
                    >
                      {isLoadingPortal ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-3 h-3" />
                          Manage
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setUpdateQuantityInput(locationCount || subscription.quantity || 1);
                        setShowUpdateQuantityModal(true);
                      }}
                      className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs font-medium"
                    >
                      Update Locations
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* All Plans Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Available Plans</h3>
                <p className="mt-1 text-sm text-gray-600">Unlimited users on every plan. Scale up as you add sites.</p>
                <div className="mt-3 flex items-center">
                  <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-primary-100 bg-primary-50 text-primary-700">
                    <Clock className="w-3 h-3 text-primary-600" /> 30-day free trial on all plans
                  </span>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { 
                    name: "Communicate", 
                    price: "Free", 
                    originalPrice: null, 
                    showDiscount: false,
                    subtitle: "Effortless team communication. No phone numbers required.",
                    description: null,
                    features: [
                      "Unlimited users",
                      "Direct & group messaging",
                      "Automatic English ↔ Spanish translation, with inbuilt google translation",
                      "Voice, photo & video messages",
                      "File attachments",
                      "Instant notifications",
                      "Add shift notes to group chats",
                      "No personal phone numbers needed — login with email (kept hidden)",
                      "Works everywhere — iOS, Android & Web"
                    ]
                  },
                  { 
                    name: "Operate", 
                    price: "$39", 
                    originalPrice: "$59", 
                    showDiscount: true,
                    subtitle: "Turn communication into action",
                    description: "Everything in Communicate, plus:",
                    features: [
                      "Custom checklists",
                      "Build your own forms (orders, inventory, issues)",
                      "AI-generated forms & checklists",
                      "Turn messages into tasks",
                      "Add ad-hoc tasks in seconds",
                      "Task conversations inside chat",
                      "Asset management + QR codes",
                      "Full Spanish-language support",
                      "Ask AI agent"
                    ]
                  },
                  { name: "Comply", price: "$99", originalPrice: "$129", showDiscount: true, features: ["Everything in Operate", "Create compliance requirements once, apply to all locations", "Automatic compliance task generation", "Contracts management", "AI contract & document analysis", "Automated reminders & escalations", "Compliance scorecards", "Audit-ready evidence vault", "Evidence (200 GB)", "ChatGPT custom GPT support"] }
                ].map((tier, i) => (
                  <div 
                    key={tier.name} 
                    className={`rounded-2xl border bg-white p-6 hover:shadow-md transition-shadow flex flex-col ${subscription?.plan?.toLowerCase() === tier.name.toLowerCase() ? 'ring-2 ring-green-500 border-green-300' : ''}`}
                  >
                    <div className={`font-semibold text-lg text-gray-900`}>
                      {tier.name} {tier.price && <span className="text-sm font-normal text-gray-600">— {tier.price} / site / mo</span>}
                      {subscription?.plan?.toLowerCase() === tier.name.toLowerCase() && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Current Plan</span>
                      )}
                    </div>
                    {tier.subtitle && (
                      <div className="mt-2">
                        <div className="font-semibold text-base text-gray-900">{tier.subtitle}</div>
                        {tier.description && (
                          <p className="text-sm text-gray-600 mt-1">{tier.description}</p>
                        )}
                      </div>
                    )}
                    {!tier.subtitle && (
                      <div className="mt-3">
                        {tier.showDiscount && tier.originalPrice ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="text-lg text-gray-400 line-through">{tier.originalPrice}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">Launch Discount</span>
                            </div>
                            <div className="text-3xl font-semibold text-gray-900 mt-1">{tier.price}<span className="text-sm text-gray-500 font-normal">/site/mo</span></div>
                          </div>
                        ) : (
                          <div className="text-3xl font-semibold text-gray-900">{tier.price}<span className="text-sm text-gray-500 font-normal">/site/mo</span></div>
                        )}
                      </div>
                    )}
                    {tier.subtitle && tier.price && tier.price !== "Free" && (
                      <div className="mt-3">
                        {tier.showDiscount && tier.originalPrice ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="text-lg text-gray-400 line-through">{tier.originalPrice}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">Launch Discount</span>
                            </div>
                            <div className="text-3xl font-semibold text-gray-900 mt-1">{tier.price}<span className="text-sm text-gray-500 font-normal">/site/mo</span></div>
                          </div>
                        ) : (
                          <div className="text-3xl font-semibold text-gray-900">{tier.price}<span className="text-sm text-gray-500 font-normal">/site/mo</span></div>
                        )}
                      </div>
                    )}
                    {tier.subtitle && tier.price === "Free" && (
                      <div className="mt-3">
                        <div className="text-3xl font-semibold text-gray-900">{tier.price}</div>
                      </div>
                    )}
                    <ul className="mt-4 text-sm text-gray-700 space-y-3 flex-grow">
                      {tier.features.map((feature, j) => {
                        // Handle string format (simple list items)
                        if (typeof feature === 'string') {
                          const isChatGPTFeature = feature.toLowerCase().includes('chatgpt');
                          return (
                            <li key={j} className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-primary-600 flex-shrink-0" />
                              <span className="flex items-center gap-1.5">
                                {feature}
                                {isChatGPTFeature && !chatGPTLogoError && (
                                  <img 
                                    src={chatGPTLogo}
                                    alt="ChatGPT" 
                                    className="w-4 h-4 flex-shrink-0 object-contain" 
                                    onError={(e) => {
                                      console.error('ChatGPT logo failed to load. Tried path:', chatGPTLogo);
                                      setChatGPTLogoError(true);
                                    }}
                                  />
                                )}
                              </span>
                            </li>
                          );
                        }
                        // Handle object format with icon, text, and description
                        const featureObj = feature as { icon?: string; text: string; description?: string };
                        return (
                          <li key={j} className="flex items-start gap-2">
                            {featureObj.icon && <span className="text-base flex-shrink-0 mt-0.5">{featureObj.icon}</span>}
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{featureObj.text}</div>
                              {featureObj.description && (
                                <div className="text-xs text-gray-600 mt-0.5">{featureObj.description}</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <button 
                      onClick={() => handleChoosePlan(tier.name)} 
                      disabled={isLoadingCheckout !== null || subscription?.plan?.toLowerCase() === tier.name.toLowerCase() || (tier.name.toLowerCase() === 'comply' && (subscription?.plan?.toLowerCase() === 'standard' || subscription?.plan?.toLowerCase() === 'pro'))}
                      className={`w-full mt-5 px-3 py-2 rounded-xl text-white text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        subscription?.plan?.toLowerCase() === tier.name.toLowerCase() || (tier.name.toLowerCase() === 'comply' && (subscription?.plan?.toLowerCase() === 'standard' || subscription?.plan?.toLowerCase() === 'pro'))
                          ? 'bg-green-600 hover:bg-green-700' 
                          : tier.name.toLowerCase() === 'communicate'
                            ? 'bg-green-600 hover:bg-green-700'
                            : i === 1 
                              ? 'bg-primary-600 hover:bg-primary-700' 
                              : 'bg-gray-900 hover:bg-gray-800'
                      }`}
                    >
                      {isLoadingCheckout === tier.name.toLowerCase() 
                        ? 'Loading...' 
                        : subscription?.plan?.toLowerCase() === tier.name.toLowerCase() || (tier.name.toLowerCase() === 'comply' && (subscription?.plan?.toLowerCase() === 'standard' || subscription?.plan?.toLowerCase() === 'pro'))
                          ? 'Current Plan'
                          : tier.name.toLowerCase() === 'communicate'
                            ? 'Get Started Free'
                            : `Choose ${tier.name}`
                      }
                    </button>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-4 text-center">
                Add‑ons: extra evidence storage, onboarding services, custom templates.
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">You are using Communicate tier</h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
              Upgrade to Operate or Comply plan to unlock all features including compliance management, task creation, asset management, and more.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/home')}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Back to Home
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pricing Plans Section - Always show for free tier users */}
      {(() => {
        // Check if user is on free (communicate) plan or has no subscription
        const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
        const isFreePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate';
        return isFreePlan || !hasActiveSubscription;
      })() && (
        <div className="card" data-plans-section>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Simple, per‑location pricing</h2>
            <p className="mt-2 text-gray-600">Unlimited users on every plan. Scale up as you add sites.</p>
            <div className="mt-3 flex items-center justify-center">
              <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-primary-100 bg-primary-50 text-primary-700">
                <Clock className="w-3 h-3 text-primary-600" /> 30-day free trial on all plans
              </span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { 
                name: "Communicate", 
                price: "Free", 
                originalPrice: null, 
                showDiscount: false,
                subtitle: "Effortless team communication. No phone numbers required.",
                description: null,
                features: [
                  "Unlimited users",
                  "Direct & group messaging",
                  "Automatic English ↔ Spanish translation",
                  "Voice, photo & video messages",
                  "File attachments",
                  "Instant notifications",
                  "Add shift notes to group chats",
                  "No personal phone numbers needed — login with email (kept hidden)",
                  "Works everywhere — iOS, Android & Web"
                ]
              },
              { 
                name: "Operate", 
                price: "$39", 
                originalPrice: "$59", 
                showDiscount: true,
                subtitle: "Turn communication into action",
                description: "Everything in Communicate, plus:",
                features: [
                  "Custom checklists",
                  "Build your own forms (orders, inventory, issues)",
                  "AI-generated forms & checklists",
                  "Turn messages into tasks",
                  "Add ad-hoc tasks in seconds",
                  "Task conversations inside chat",
                  "Asset management + QR codes",
                  "Full Spanish-language support",
                  "Ask AI agent"
                ]
              },
              { name: "Comply", price: "$99", originalPrice: "$129", showDiscount: true, features: ["Everything in Operate", "Create compliance requirements once, apply to all locations", "Automatic compliance task generation", "Contracts management", "AI contract & document analysis", "Automated reminders & escalations", "Compliance scorecards", "Audit-ready evidence vault", "Evidence (200 GB)", "ChatGPT custom GPT support"] }
            ].map((tier, i) => (
              <div 
                key={tier.name} 
                className={`rounded-2xl border bg-white p-6 hover:shadow-md transition-shadow flex flex-col`}
              >
                <div className={`font-semibold text-lg text-gray-900`}>
                  {tier.name} {tier.price && <span className="text-sm font-normal text-gray-600">— {tier.price} / site / mo</span>}
                </div>
                {tier.subtitle && (
                  <div className="mt-2">
                    <div className="font-semibold text-base text-gray-900">{tier.subtitle}</div>
                    {tier.description && (
                      <p className="text-sm text-gray-600 mt-1">{tier.description}</p>
                    )}
                  </div>
                )}
                {!tier.subtitle && (
                  <div className="mt-3">
                    {tier.showDiscount && tier.originalPrice ? (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-lg text-gray-400 line-through">{tier.originalPrice}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">Launch Discount</span>
                        </div>
                        <div className="text-3xl font-semibold text-gray-900 mt-1">{tier.price}<span className="text-sm text-gray-500 font-normal">/site/mo</span></div>
                      </div>
                    ) : (
                      <div className="text-3xl font-semibold text-gray-900">{tier.price}<span className="text-sm text-gray-500 font-normal">/site/mo</span></div>
                    )}
                  </div>
                )}
                {tier.subtitle && tier.price && tier.price !== "Free" && (
                  <div className="mt-3">
                    {tier.showDiscount && tier.originalPrice ? (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-lg text-gray-400 line-through">{tier.originalPrice}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">Launch Discount</span>
                        </div>
                        <div className="text-3xl font-semibold text-gray-900 mt-1">{tier.price}<span className="text-sm text-gray-500 font-normal">/site/mo</span></div>
                      </div>
                    ) : (
                      <div className="text-3xl font-semibold text-gray-900">{tier.price}<span className="text-sm text-gray-500 font-normal">/site/mo</span></div>
                    )}
                  </div>
                )}
                {tier.subtitle && tier.price === "Free" && (
                  <div className="mt-3">
                    <div className="text-3xl font-semibold text-gray-900">{tier.price}</div>
                  </div>
                )}
                <ul className="mt-4 text-sm text-gray-700 space-y-3 flex-grow">
                  {tier.features.map((feature, j) => {
                    // Handle string format (simple list items)
                    if (typeof feature === 'string') {
                      const isChatGPTFeature = feature.toLowerCase().includes('chatgpt');
                      return (
                        <li key={j} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary-600 flex-shrink-0" />
                          <span className="flex items-center gap-1.5">
                            {feature}
                            {isChatGPTFeature && !chatGPTLogoError && (
                              <img 
                                src={chatGPTLogo}
                                alt="ChatGPT" 
                                className="w-4 h-4 flex-shrink-0 object-contain" 
                                onError={(e) => {
                                  console.error('ChatGPT logo failed to load. Tried path:', chatGPTLogo);
                                  setChatGPTLogoError(true);
                                }}
                              />
                            )}
                          </span>
                        </li>
                      );
                    }
                    // Handle object format with icon, text, and description
                    const featureObj = feature as { icon?: string; text: string; description?: string };
                    return (
                      <li key={j} className="flex items-start gap-2">
                        {featureObj.icon && <span className="text-base flex-shrink-0 mt-0.5">{featureObj.icon}</span>}
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{featureObj.text}</div>
                          {featureObj.description && (
                            <div className="text-xs text-gray-600 mt-0.5">{featureObj.description}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <button 
                  onClick={() => handleChoosePlan(tier.name)} 
                  disabled={isLoadingCheckout !== null}
                  className={`w-full mt-5 px-3 py-2 rounded-xl text-white text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    tier.name.toLowerCase() === 'communicate'
                      ? 'bg-green-600 hover:bg-green-700'
                      : i === 1 
                        ? 'bg-primary-600 hover:bg-primary-700' 
                        : 'bg-gray-900 hover:bg-gray-800'
                  }`}
                >
                  {isLoadingCheckout === tier.name.toLowerCase() ? 'Loading...' : tier.name.toLowerCase() === 'communicate' ? 'Get Started Free' : `Choose ${tier.name}`}
                </button>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-4 text-center">
            Add‑ons: extra evidence storage, onboarding services, custom templates.
          </div>
        </div>
      )}

      {/* Quantity Input Modal */}
      {showQuantityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enter Number of Locations</h3>
            <p className="text-sm text-gray-600 mb-4">
              How many locations do you need to subscribe for? Your subscription will be billed per location per month.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Locations
              </label>
              <input
                type="number"
                min="1"
                value={quantityInput}
                onChange={(e) => setQuantityInput(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
              {locationCount > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Your organization currently has {locationCount} location{locationCount !== 1 ? 's' : ''}.
                </p>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowQuantityModal(false);
                  setSelectedPlan(null);
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmQuantity}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Continue to Checkout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Quantity Modal */}
      {showUpdateQuantityModal && subscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Location License Count</h3>
            <p className="text-sm text-gray-600 mb-4">
              Your subscription will be updated immediately. You'll be charged prorated amount based on your current billing cycle.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current License Count: {subscription.quantity || 1}
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Locations
              </label>
              <input
                type="number"
                min="1"
                value={updateQuantityInput}
                onChange={(e) => setUpdateQuantityInput(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
              {locationCount > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Your organization currently has {locationCount} location{locationCount !== 1 ? 's' : ''}.
                </p>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowUpdateQuantityModal(false);
                  setUpdateQuantityInput(1);
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={isUpdatingQuantity}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateQuantity}
                disabled={isUpdatingQuantity}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUpdatingQuantity ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Updating...
                  </>
                ) : (
                  'Update Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Warning Modal */}
      {showUpgradeModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowUpgradeModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Cannot Reduce Licenses</h3>
                <p className="text-sm text-gray-600">Action Required</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              {upgradeModalMessage}
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/locations');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                <MapPin className="w-4 h-4" />
                Go to Locations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Required Modal */}
      {showOrgModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowOrgModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Organization Required</h3>
                <p className="text-sm text-gray-600">Action Required</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              You must create an organization before you can subscribe to a plan. Please create an organization first.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowOrgModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowOrgModal(false);
                  navigate('/organizations');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                Go to Organizations
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

